/**
 * Pressure control — the SHARED, cross-vertical BOP certificate module
 * (fieldiq-api#1268, BOP-1, migration 0575), mounted at /api/v1/pressure-control.
 *
 * The manifest records GET /meta, GET and PUT /settings, GET /components (an
 * empty array for the probe tenant) and GET /equipment/:id/status (null data).
 * Every by-id read and write records a refusal for the probe's unknown id, so
 * the component, certification and event shapes below are typed by hand from
 * the route file at api origin/main eb686f61 (shapeComponent, shapeCert,
 * shapeEvent, shapeState). Reads are camelCase (the office surface); writes
 * take snake_case keys. Pressure is in the tenant's unit (`pressureUnit`).
 */
import type { ApiEnvelope } from '../lib/api-envelope';

/**
 * A copy of the API's COMPONENT_CLASSES (lib/pressure-control/cert-state.ts),
 * which the CHECK on pressure_control_specs and pc_test_defaults mirrors. The
 * last ten are the Wireline & Slickline classes (api#1333, WL-0, 0610).
 */
export const PC_COMPONENT_CLASSES = [
  'annular', 'pipe_ram', 'blind_ram', 'blind_shear_ram', 'spool', 'adapter_flange', 'ram_block',
  'carrier', 'bleed_off_hose', 'kill_hose', 'choke_manifold', 'accumulator', 'stack_assembly', 'other',
  'lubricator', 'grease_injection_head', 'stuffing_box', 'pack_off', 'line_wiper',
  'wireline_valve', 'pump_in_sub', 'tool_trap', 'tool_catcher', 'wellhead_adapter',
] as const;
export type PcComponentClass = (typeof PC_COMPONENT_CLASSES)[number];

export type PcCertStatus = 'uncertified' | 'recert_required' | 'expired' | 'due_soon' | 'in_storage' | 'valid';
export const PC_CERT_STATUSES: readonly PcCertStatus[] = [
  'uncertified', 'recert_required', 'expired', 'due_soon', 'in_storage', 'valid',
];

export type PcEventKind = 'placed_in_service' | 'returned_to_storage' | 'elastomers_installed' | 'recert_trigger';
export const PC_EVENT_KINDS: readonly PcEventKind[] = [
  'placed_in_service', 'returned_to_storage', 'elastomers_installed', 'recert_trigger',
];

export type PcTriggerReason = 'blowout' | 'over_pressure' | 'sour_exposure' | 'well_control_event';
export const PC_TRIGGER_REASONS: readonly PcTriggerReason[] = [
  'blowout', 'over_pressure', 'sour_exposure', 'well_control_event',
];

/** Every response's `meta` block — the tenant's day, zone and pressure unit. */
export interface PcResponseMeta {
  today: string;
  timeZone: string;
  unitSystem: string;
  pressureUnit: string;
}

/** A /pressure-control response: the envelope plus the tenant's day / zone / pressure unit. */
export type PcEnvelope<T> = ApiEnvelope<T> & { meta?: PcResponseMeta };

/** GET /pressure-control/meta → data. */
export interface PcMeta {
  componentClasses: Array<{ key: PcComponentClass | string; defaultHasElastomers: boolean | null }>;
  serviceEventKinds: string[];
  recertTriggerReasons: string[];
  certStatuses: string[];
  defaults: { maxInServiceYears: number; maxTotalYears: number; warnDays: number };
}

/** GET / PUT /pressure-control/settings → data. `stored` is false when the tenant has no row. */
export interface PcSettings {
  maxInServiceYears: number;
  maxTotalYears: number;
  warnDays: number;
  stored: boolean;
}

export interface PcSettingsWrite {
  max_in_service_years?: number;
  max_total_years?: number;
  warn_days?: number;
}

/** shapeState. `expiresOn` is the LAST VALID DAY; `daysUntilExpiry` is negative once expired. */
export interface PcCertState {
  status: PcCertStatus | string;
  inService: boolean;
  clockStartedOn: string | null;
  elastomerRule: boolean;
  expiresOn: string | null;
  /** BOP-2: the expiry WITHOUT a D036 extension, and the extension's last day (null when none applies). */
  baseExpiresOn?: string | null;
  extendedThrough?: string | null;
  expiryBasis: 'in_service' | 'total_life' | null;
  daysUntilExpiry: number | null;
  recertTriggers: Array<{ reason: string; occurredOn: string }>;
}

/** shapeCert. Append-only; the year limits are snapshotted from settings at entry. */
export interface PcCertification {
  id: string;
  certifiedOn: string;
  inServiceOn: string | null;
  certifyingFacility: string | null;
  certifyingEngineer: string | null;
  certificateNumber: string | null;
  documentUploadId: string | null;
  maxInServiceYears: number;
  maxTotalYears: number;
  notes: string | null;
  createdBy: string | null;
  createdAt: string | null;
}

/** shapeEvent. Append-only. */
export interface PcServiceEvent {
  id: string;
  eventKind: PcEventKind | string;
  triggerReason: PcTriggerReason | string | null;
  occurredOn: string;
  contextRef: string | null;
  notes: string | null;
  recordedBy: string | null;
  createdAt: string | null;
}

export interface PcSpec {
  id: string;
  componentClass: PcComponentClass | string;
  hasElastomers: boolean;
  boreSize: string | null;
  workingPressure: number | null;
  pressureUnit: string;
  naceMr0175: boolean;
  stackParentId: string | null;
  stackParentName: string | null;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** shapeComponent — one row of GET /components, and GET /equipment/:id/status's data. */
export interface PcComponent {
  equipmentId: string;
  name: string;
  type: string | null;
  unitNumber: string | null;
  serialNumber: string | null;
  manufacturer: string | null;
  model: string | null;
  equipmentStatus: string;
  spec: PcSpec;
  currentCertification: PcCertification | null;
  certState: PcCertState;
}

/** GET /components/:id → data. Certifications newest first; events newest first. */
export interface PcComponentDetail extends PcComponent {
  certifications: PcCertification[];
  events: PcServiceEvent[];
  stackComponents: Array<{ equipmentId: string; name: string; componentClass: string }>;
}

export interface PcComponentWrite {
  equipment_id?: string;
  component_class?: string;
  has_elastomers?: boolean | null;
  bore_size?: string | null;
  working_pressure?: number | null;
  nace_mr0175?: boolean;
  stack_parent_id?: string | null;
  notes?: string | null;
}

export interface PcCertificationWrite {
  certified_on: string;
  in_service_on?: string | null;
  certifying_facility?: string | null;
  certifying_engineer?: string | null;
  certificate_number?: string | null;
  document_upload_id?: string | null;
  notes?: string | null;
}

export interface PcEventWrite {
  event_kind: string;
  trigger_reason?: string | null;
  occurred_on: string;
  context_ref?: string | null;
  notes?: string | null;
}

// ─── BOP-2 (fieldiq-api#1281, migration 0585) ────────────────────────────────
// Typed by hand from src/routes/pressure-control-records.routes.ts and
// src/lib/pressure-control/records.ts at api 45d7bfe6. Pressures are in the
// tenant's unit (`pressureUnit`); days are 'YYYY-MM-DD'; instants are ISO.
// Calibrations, tests, drills and extensions are APPEND-ONLY on the API.

/** `rig_up` (api#1333, WL-0): the on-site test after each rig-up, before running in. */
export type PcTestKind = 'installation' | 'periodic' | 'after_repair' | 'elastomers_installed' | 'other' | 'rig_up';
export const PC_TEST_KINDS: readonly PcTestKind[] = ['installation', 'periodic', 'after_repair', 'elastomers_installed', 'other', 'rig_up'];

/** `wireline_shut_in` (api#1333, WL-0): a wireline crew's shut-in drill. */
export type PcDrillKind = 'drilling' | 'tripping' | 'out_of_hole' | 'verbal' | 'other' | 'wireline_shut_in';
export const PC_DRILL_KINDS: readonly PcDrillKind[] = ['drilling', 'tripping', 'out_of_hole', 'verbal', 'other', 'wireline_shut_in'];

export type PcCalibrationStatus = 'valid' | 'due_soon' | 'overdue' | 'never';
export const PC_CALIBRATION_STATUSES: readonly PcCalibrationStatus[] = ['valid', 'due_soon', 'overdue', 'never'];

export type PcDrillStatusKind = 'never' | 'current' | 'overdue';

/** shapeCalibration. `dueOn` is the LAST valid day. */
export interface PcCalibration {
  id: string;
  equipmentId: string;
  calibratedOn: string;
  dueOn: string;
  rangeMin: number | null;
  rangeMax: number | null;
  pressureUnit: string;
  accuracyClass: string | null;
  certificateNumber: string | null;
  performedBy: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string | null;
  state: { status: PcCalibrationStatus | string; daysUntilDue: number | null; alertTier: number | null };
}

/** GET /equipment/:id/calibration → data. History newest first; `current` is history[0]. */
export interface PcGaugeCalibration {
  equipmentId: string;
  status: PcCalibrationStatus | string;
  daysUntilDue: number | null;
  alertTier: number | null;
  current: PcCalibration | null;
  history: PcCalibration[];
}

export interface PcCalibrationWrite {
  equipment_id: string;
  calibrated_on: string;
  due_on: string;
  range_min?: number | null;
  range_max: number;
  accuracy_class?: string | null;
  certificate_number?: string | null;
  performed_by?: string | null;
  notes?: string | null;
}

/** shapeItem. `*TestPressure` is in the tenant's unit; `*Entered` / `*Unit` is what was typed. */
export interface PcTestItem {
  id: string;
  equipmentId: string;
  name: string | null;
  componentClass: string | null;
  lowTestPressure: number | null;
  lowTestEntered: number | null;
  lowTestUnit: string | null;
  lowHoldMinutes: number | null;
  highTestPressure: number | null;
  highTestEntered: number | null;
  highTestUnit: string | null;
  highHoldMinutes: number | null;
  result: 'pass' | 'fail' | string;
  notes: string | null;
}

/** shapeTest. The gauge block is the calibration SNAPSHOT taken when the test was recorded. */
export interface PcTest {
  id: string;
  testedAt: string | null;
  testedOn: string | null;
  testKind: PcTestKind | string;
  stackEquipmentId: string | null;
  stackName: string | null;
  gauge: {
    equipmentId: string | null;
    name: string | null;
    calibrationId: string | null;
    calibratedOn: string | null;
    dueOn: string | null;
    rangeMax: number | null;
  };
  pressureUnit: string;
  chartUploadId: string | null;
  testedByUserId: string | null;
  testedByUserName: string | null;
  testedByName: string | null;
  witnessedBy: string | null;
  contextRef: string | null;
  result: 'pass' | 'fail' | string;
  notes: string | null;
  recordedBy: string | null;
  createdAt: string | null;
  /** fieldiq-api#1324 (0605): a record made on a WS tour sheet is pending until the office approves the sheet. */
  reviewStatus: PcReviewStatus | string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  items: PcTestItem[];
}

export type PcReviewStatus = 'pending' | 'approved';

export interface PcTestItemWrite {
  equipment_id: string;
  low_test_pressure?: number | null;
  low_hold_minutes?: number | null;
  high_test_pressure: number;
  high_hold_minutes: number;
  result: 'pass' | 'fail';
  notes?: string | null;
}

export interface PcTestWrite {
  /** Client-minted, so a chart can be uploaded against it first. */
  id?: string;
  tested_at: string;
  test_kind: string;
  items: PcTestItemWrite[];
  gauge_equipment_id: string;
  stack_equipment_id?: string | null;
  tested_by_user_id?: string | null;
  tested_by_name?: string | null;
  witnessed_by?: string | null;
  context_ref?: string | null;
  chart_upload_id?: string | null;
  notes?: string | null;
}

/** A 409 from POST /tests: a gauge refusal (code + field) or the components' per-unit failures. */
export interface PcTestRefusal {
  error: string;
  code?: string;
  field?: string;
  failures?: Array<{ equipmentId: string; code: string; error: string }>;
}

/** shapeDrill. */
export interface PcDrill {
  id: string;
  drilledAt: string | null;
  drilledOn: string | null;
  drillKind: PcDrillKind | string;
  secondsToSecure: number | null;
  rigEquipmentId: string | null;
  rigName: string | null;
  stackEquipmentId: string | null;
  stackName: string | null;
  contextRef: string | null;
  notes: string | null;
  recordedBy: string | null;
  createdAt: string | null;
  reviewStatus: PcReviewStatus | string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  crew: Array<{ userId: string; name: string | null }>;
}

export interface PcDrillWrite {
  drilled_at: string;
  drill_kind: string;
  seconds_to_secure?: number | null;
  crew_user_ids: string[];
  rig_equipment_id?: string | null;
  stack_equipment_id?: string | null;
  context_ref?: string | null;
  notes?: string | null;
}

/** One crew member's or rig's drill standing (drillStatusFor). Warns only. */
export interface PcDrillStanding {
  id: string;
  name: string | null;
  status: PcDrillStatusKind | string;
  lastDrilledOn: string | null;
  daysSince: number | null;
  dueBy: string | null;
}

export interface PcDrillWarning {
  kind: 'user' | 'equipment';
  id: string;
  status: string;
  message: string;
}

/** GET /drills/status → data, and POST /drills → data.status. */
export interface PcDrillStatus {
  warnDays: number;
  crew: PcDrillStanding[];
  rigs: PcDrillStanding[];
  notFound: { userIds: string[]; equipmentIds: string[] };
  warnings: PcDrillWarning[];
}

/** GET / PUT /drill-settings → data. `stored` is false when the tenant has no row. */
export interface PcDrillSettings {
  drillWarnDays: number;
  stored: boolean;
}

/** shapeDefault. Exactly one of componentClass / equipmentId is set. */
export interface PcTestDefault {
  id: string;
  componentClass: string | null;
  equipmentId: string | null;
  equipmentName: string | null;
  lowTestPressure: number | null;
  highTestPressure: number | null;
  pressureUnit: string;
  lowHoldMinutes: number | null;
  highHoldMinutes: number | null;
  updatedBy: string | null;
  updatedAt: string | null;
  /** Only on GET /components/:id/test-defaults: whose defaults these are. */
  source?: 'component' | 'class';
}

export interface PcTestDefaultWrite {
  component_class?: string | null;
  equipment_id?: string | null;
  low_test_pressure?: number | null;
  high_test_pressure?: number | null;
  low_hold_minutes?: number | null;
  high_hold_minutes?: number | null;
}

/** shapeExtension — a D036 Appendix 5 extension of the three-year in-service expiry. */
export interface PcExtension {
  id: string;
  certificationId: string;
  equipmentId: string;
  baseExpiresOn: string;
  extendedThrough: string;
  grantedOn: string;
  reason: string;
  contextRef: string;
  grantedBy: string | null;
  createdAt: string | null;
}

export interface PcExtensionWrite {
  extended_through: string;
  reason: string;
  context_ref: string;
}

/** GET /status → data: the module at a glance. Warnings only. */
export interface PcModuleStatus {
  components: { total: number; byStatus: Partial<Record<PcCertStatus | string, number>>; extended: number };
  gaugesNeedingCalibration: Array<{ equipmentId: string; name: string | null; dueOn: string | null; status: string; daysUntilDue: number | null }>;
  drills: { warnDays: number; rigs: PcDrillStanding[]; warnings: PcDrillWarning[] };
}

// ─── WL-0 (fieldiq-api#1333, migration 0610): certificate rules by class ─────
// Typed by hand from src/routes/pressure-control.routes.ts at api 7195662c
// (shapeClassRule and GET /class-rules). Reads camelCase; the PUT takes
// snake_case. A rule is a TENANT DEFAULT, never a regulation value.

/** shapeClassRule — the stored rule for one class. */
export interface PcClassRule {
  componentClass: PcComponentClass | string;
  maxInServiceYears: number;
  maxTotalYears: number;
  notes: string | null;
  /** The API's CLASS_RULE_LABEL, 'tenant default'. */
  label: string;
  updatedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** GET /pressure-control/class-rules → data[]: every class, its rule if any, and what a new certificate would snapshot. */
export interface PcClassRuleRow {
  componentClass: PcComponentClass | string;
  rule: PcClassRule | null;
  effective: {
    maxInServiceYears: number;
    maxTotalYears: number;
    source: 'class_rule' | 'company_settings' | string;
  };
}

/**
 * PUT /pressure-control/class-rules/:componentClass. Each year a whole number
 * 1–20, in service ≤ total. A null year is refused by the API with a 400 that
 * names the field, which is how an unparseable entry reaches the reader.
 * Omitting `notes` keeps the stored note; null clears it.
 */
export interface PcClassRuleWrite {
  max_in_service_years: number | null;
  max_total_years: number | null;
  notes?: string | null;
}
